import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search, RefreshCw, FileText, StickyNote, Send, Download, Loader2,
  Inbox, ExternalLink, User, MapPin, HardHat, Paperclip, Trash2, Lock, Settings, KeyRound,
  MessageSquare, ClipboardList, Phone, Mail, Image as ImageIcon, Upload, RotateCcw,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Header } from "@/components/Header";
import { TopoBackground } from "@/components/TopoBackground";
import { STATUSES, STATUS_STYLES, INQUIRY_STATUSES, INQUIRY_STATUS_STYLES, PROJECT_CATS, PROJECT_DETAIL_FIELDS } from "@/lib/constants";
import { useAdminAuth } from "@/context/AdminAuth";
import {
  listRequests, getStats, updateStatus, addNote, sendQuotation, fileUrl,
  verifyAdminCode, deleteRequest, updateCredentials,
  listInquiries, getInquiryStats, updateInquiryStatus, deleteInquiry,
  getSiteContact, updateSiteContact,
  getSiteImages, setSiteImage, deleteSiteImage, uploadFile,
  getProjects, updateProject,
} from "@/lib/api";
import { SITE_IMAGE_GROUPS, resolveImage } from "@/lib/siteImages";

const StatusBadge = ({ status }) => (
  <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${STATUS_STYLES[status] || ""}`}>
    {status}
  </span>
);

const Section = ({ icon: Icon, title, children }) => (
  <div className="rounded-xl border border-[#e6eae7] bg-white p-4">
    <div className="mb-3 flex items-center gap-2">
      <Icon className="h-4 w-4 text-[#b38f3f]" />
      <h3 className="font-display text-sm font-semibold uppercase tracking-wide text-[#163323]">{title}</h3>
    </div>
    {children}
  </div>
);

const Item = ({ k, v }) => (
  <div className="flex flex-col border-b border-[#f0f2f0] py-1.5 last:border-0 sm:flex-row sm:justify-between sm:gap-4">
    <span className="text-xs text-[#5a6660]">{k}</span>
    <span className="break-words text-sm text-[#1a221e] sm:text-right">{v || "—"}</span>
  </div>
);

function CredentialsDialog({ open, onOpenChange }) {
  const [current, setCurrent] = useState("");
  const [code, setCode] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) { setCurrent(""); setCode(""); setEmail(""); setPassword(""); setBusy(false); }
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    if (!current) return toast.error("Enter your current password");
    if (code && !/^\d{4}$/.test(code)) return toast.error("Access code must be exactly 4 digits");
    if (password && password.length < 6) return toast.error("New password must be at least 6 characters");
    if (!code && !email && !password) return toast.error("Enter at least one new value to update");
    const payload = { current_password: current };
    if (code) payload.code = code;
    if (email) payload.email = email;
    if (password) payload.password = password;
    setBusy(true);
    try {
      await updateCredentials(payload);
      toast.success("Access credentials updated");
      onOpenChange(false);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error((d && (d.message || d)) || "Could not update credentials");
    } finally {
      setBusy(false);
    }
  };

  const fld = "h-11 rounded-lg border-[#d2dad5] focus-visible:ring-2 focus-visible:ring-[#c8a452] focus-visible:ring-offset-0";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="credentials-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display text-xl text-[#163323]">
            <KeyRound className="h-5 w-5 text-[#b38f3f]" /> Access Settings
          </DialogTitle>
          <DialogDescription>
            Update your admin access code, email, or password. Enter your current password to confirm changes.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-sm font-medium text-[#1a221e]">Current Password <span className="text-[#c0392b]">*</span></Label>
            <Input data-testid="cred-current-password" type="password" value={current}
              onChange={(e) => setCurrent(e.target.value)} placeholder="Enter current password" className={fld} />
          </div>
          <div className="h-px bg-[#e6eae7]" />
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label className="text-sm font-medium text-[#1a221e]">New 4-Digit Code</Label>
              <Input data-testid="cred-new-code" inputMode="numeric" maxLength={4} value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 4))} placeholder="e.g. 4920" className={fld} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-medium text-[#1a221e]">New Password</Label>
              <Input data-testid="cred-new-password" type="password" value={password}
                onChange={(e) => setPassword(e.target.value)} placeholder="Min 6 characters" className={fld} />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm font-medium text-[#1a221e]">New Access Email</Label>
            <Input data-testid="cred-new-email" type="email" value={email}
              onChange={(e) => setEmail(e.target.value)} placeholder="admin@example.com" className={fld} />
          </div>
          <p className="text-xs text-[#9aa5a0]">Leave any field blank to keep it unchanged.</p>
          <Button data-testid="cred-save-btn" type="submit" disabled={busy}
            className="h-11 w-full rounded-lg bg-[#163323] font-semibold text-white hover:bg-[#0d2116] disabled:opacity-60">
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Save Changes"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ContactSettingsDialog({ open, onOpenChange }) {
  const [c, setC] = useState({ phone: "", whatsapp: "", email: "", service_area: "", office_hours: "", response_time: "" });
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setLoading(true);
      getSiteContact().then((d) => setC({ phone: "", whatsapp: "", email: "", service_area: "", office_hours: "", response_time: "", ...d }))
        .catch(() => {}).finally(() => setLoading(false));
    }
  }, [open]);

  const upd = (k, v) => setC((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await updateSiteContact(c);
      toast.success("Contact information updated");
      onOpenChange(false);
    } catch {
      toast.error("Could not update contact information");
    } finally {
      setBusy(false);
    }
  };

  const fld = "h-11 rounded-lg border-[#d2dad5] focus-visible:ring-2 focus-visible:ring-[#c8a452] focus-visible:ring-offset-0";
  const fields = [
    ["phone", "Business Phone Number", "e.g. +1 (242) 000-0000"],
    ["whatsapp", "WhatsApp Number", "e.g. +1 242 000 0000"],
    ["email", "Business Email", "e.g. info@example.com"],
    ["service_area", "Service Area", "e.g. Nassau & Family Islands"],
    ["office_hours", "Office Hours", "e.g. Mon–Fri, 8am–5pm"],
    ["response_time", "Expected Response Time", "e.g. Within 1 business day"],
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="contact-settings-dialog" className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display text-xl text-[#163323]">
            <Phone className="h-5 w-5 text-[#b38f3f]" /> Contact Information
          </DialogTitle>
          <DialogDescription>
            These details appear on the public Contact page. Quick-contact buttons activate once phone, WhatsApp and email are filled in.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          {loading ? (
            <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-[#c8a452]" /></div>
          ) : fields.map(([key, label, ph]) => (
            <div key={key} className="space-y-1.5">
              <Label className="text-sm font-medium text-[#1a221e]">{label}</Label>
              <Input data-testid={`site-contact-${key}`} value={c[key]} onChange={(e) => upd(key, e.target.value)}
                placeholder={ph} className={fld} />
            </div>
          ))}
          <Button data-testid="site-contact-save" type="submit" disabled={busy}
            className="h-11 w-full rounded-lg bg-[#163323] font-semibold text-white hover:bg-[#0d2116] disabled:opacity-60">
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Save Contact Info"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); }
  catch { return iso; }
};

export default function Admin() {
  const { authed, openModal } = useAdminAuth();
  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState({});
  const [filter, setFilter] = useState("All");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [showCreds, setShowCreds] = useState(false);
  const [showContact, setShowContact] = useState(false);
  const [view, setView] = useState("requests");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [reqs, st] = await Promise.all([listRequests(filter, search), getStats()]);
      setRequests(reqs);
      setStats(st);
    } catch {
      toast.error("Failed to load requests");
    } finally {
      setLoading(false);
    }
  }, [filter, search]);

  useEffect(() => { if (authed) load(); }, [load, authed]);
  useEffect(() => { if (!authed) openModal(); }, [authed, openModal]);

  const onUpdated = (updated) => {
    setSelected(updated);
    setRequests((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
    getStats().then(setStats);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteRequest(deleteTarget.id);
      setRequests((prev) => prev.filter((r) => r.id !== deleteTarget.id));
      setSelected((s) => (s && s.id === deleteTarget.id ? null : s));
      getStats().then(setStats);
      toast.success(`Deleted ${deleteTarget.reference_number}`);
    } catch {
      toast.error("Could not delete request");
    } finally {
      setDeleteTarget(null);
    }
  };

  const tabs = ["All", ...STATUSES];

  if (!authed) return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-[#f4f5f4] px-4 text-center">
      <p className="text-sm text-[#5a6660]">Admin access is required to view this page.</p>
      <Button data-testid="open-admin-gate" onClick={openModal}
        className="mt-4 rounded-full bg-[#163323] px-6 font-semibold text-white hover:bg-[#0d2116]">
        Enter access code
      </Button>
    </div>
  );

  return (
    <>
      <Header />
      <div className="relative overflow-hidden bg-[#163323] py-8 text-white">
        <div className="mx-auto flex max-w-7xl flex-col gap-4 px-4 sm:flex-row sm:items-center sm:justify-between sm:px-6 lg:px-8">
          <div>
            <h1 className="font-display text-2xl font-bold sm:text-3xl">Admin Portal</h1>
            <p className="mt-1 text-sm text-white/70">Manage survey requests, messages, notes and quotations.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button data-testid="contact-info-btn" onClick={() => setShowContact(true)}
              variant="outline" className="rounded-full border-white/25 bg-transparent text-white hover:bg-white/10 hover:text-white">
              <Phone className="mr-2 h-4 w-4" /> Contact Info
            </Button>
            <Button data-testid="manage-credentials-btn" onClick={() => setShowCreds(true)}
              variant="outline" className="rounded-full border-white/25 bg-transparent text-white hover:bg-white/10 hover:text-white">
              <Settings className="mr-2 h-4 w-4" /> Access Settings
            </Button>
          </div>
        </div>
        <div className="mx-auto mt-5 flex max-w-7xl gap-2 px-4 sm:px-6 lg:px-8">
          <button data-testid="view-requests" onClick={() => setView("requests")}
            className={`flex items-center gap-2 rounded-full px-5 py-2 text-sm font-semibold transition-all ${
              view === "requests" ? "bg-[#c8a452] text-[#163323]" : "bg-white/10 text-white hover:bg-white/20"
            }`}>
            <ClipboardList className="h-4 w-4" /> Survey Requests
          </button>
          <button data-testid="view-messages" onClick={() => setView("messages")}
            className={`flex items-center gap-2 rounded-full px-5 py-2 text-sm font-semibold transition-all ${
              view === "messages" ? "bg-[#c8a452] text-[#163323]" : "bg-white/10 text-white hover:bg-white/20"
            }`}>
            <MessageSquare className="h-4 w-4" /> Messages
          </button>
          <button data-testid="view-photos" onClick={() => setView("photos")}
            className={`flex items-center gap-2 rounded-full px-5 py-2 text-sm font-semibold transition-all ${
              view === "photos" ? "bg-[#c8a452] text-[#163323]" : "bg-white/10 text-white hover:bg-white/20"
            }`}>
            <ImageIcon className="h-4 w-4" /> Photos
          </button>
          <button data-testid="view-projects" onClick={() => setView("projects")}
            className={`flex items-center gap-2 rounded-full px-5 py-2 text-sm font-semibold transition-all ${
              view === "projects" ? "bg-[#c8a452] text-[#163323]" : "bg-white/10 text-white hover:bg-white/20"
            }`}>
            <HardHat className="h-4 w-4" /> Projects
          </button>
        </div>
      </div>

      {view === "messages" ? (
        <InquiriesView />
      ) : view === "photos" ? (
        <SiteImagesView />
      ) : view === "projects" ? (
        <ProjectsAdminView />
      ) : (
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        {/* Stats */}
        <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-8">
          {tabs.map((t) => (
            <button key={t} data-testid={`stat-${t}`} onClick={() => setFilter(t)}
              className={`rounded-xl border p-3 text-left transition-all ${
                filter === t ? "border-[#c8a452] bg-[#f6f1e3]" : "border-[#e6eae7] bg-white hover:border-[#c8a452]/50"
              }`}>
              <div className="font-display text-2xl font-bold text-[#163323]">{stats[t] ?? 0}</div>
              <div className="mt-0.5 truncate text-xs text-[#5a6660]">{t}</div>
            </button>
          ))}
        </div>

        {/* Search + refresh */}
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#9aa5a0]" />
            <Input data-testid="admin-search" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by reference, name, email, company or island…"
              className="h-11 rounded-lg border-[#d2dad5] bg-white pl-10 focus-visible:ring-2 focus-visible:ring-[#c8a452] focus-visible:ring-offset-0" />
          </div>
          <Button data-testid="admin-refresh" onClick={load} variant="outline"
            className="h-11 rounded-lg border-[#d2dad5] text-[#163323] hover:bg-[#f2f4f2]">
            <RefreshCw className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>

        {/* List */}
        <div className="overflow-hidden rounded-xl border border-[#e6eae7] bg-white">
          <div className="hidden grid-cols-12 gap-4 border-b border-[#e6eae7] bg-[#f8f9f8] px-5 py-3 text-xs font-semibold uppercase tracking-wide text-[#5a6660] md:grid">
            <div className="col-span-2">Reference</div>
            <div className="col-span-3">Customer</div>
            <div className="col-span-2">Project</div>
            <div className="col-span-2">Island</div>
            <div className="col-span-2">Status</div>
            <div className="col-span-1 text-right">Actions</div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-16 text-[#5a6660]">
              <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading…
            </div>
          ) : requests.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-[#5a6660]">
              <Inbox className="h-10 w-10 text-[#c8d0cb]" />
              <p className="mt-3 text-sm">No requests found.</p>
            </div>
          ) : (
            requests.map((r) => (
              <div key={r.id} data-testid={`request-row-${r.reference_number}`} onClick={() => setSelected(r)}
                className="grid w-full cursor-pointer grid-cols-1 gap-2 border-b border-[#e6eae7] px-5 py-4 text-left transition-colors last:border-0 hover:bg-[#f8f9f8] md:grid-cols-12 md:items-center md:gap-4">
                <div className="col-span-2 font-mono text-sm font-semibold text-[#163323]">
                  {r.reference_number}
                  <span className="block font-sans text-xs font-normal text-[#9aa5a0]">{fmtDate(r.created_at)}</span>
                </div>
                <div className="col-span-3">
                  <div className="text-sm font-medium text-[#1a221e]">{r.customer?.full_name}</div>
                  <div className="text-xs text-[#5a6660]">{r.customer?.email}</div>
                </div>
                <div className="col-span-2 text-sm text-[#1a221e]">{r.construction?.project_type || r.service_type}</div>
                <div className="col-span-2 text-sm text-[#5a6660]">{r.location?.island}</div>
                <div className="col-span-2"><StatusBadge status={r.status} /></div>
                <div className="col-span-1 flex md:justify-end">
                  <button data-testid={`delete-request-${r.reference_number}`}
                    onClick={(e) => { e.stopPropagation(); setDeleteTarget(r); }}
                    className="rounded-lg p-2 text-[#9aa5a0] transition-colors hover:bg-[#fef2f2] hover:text-[#b91c1c]"
                    title="Delete request" aria-label="Delete request">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
      )}

      <RequestDetail request={selected} onClose={() => setSelected(null)} onUpdated={onUpdated} />

      <CredentialsDialog open={showCreds} onOpenChange={setShowCreds} />
      <ContactSettingsDialog open={showContact} onOpenChange={setShowContact} />

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this request?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes request <strong>{deleteTarget?.reference_number}</strong> from{" "}
              {deleteTarget?.customer?.full_name}. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-btn" onClick={confirmDelete}
              className="bg-[#b91c1c] text-white hover:bg-[#991b1b]">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function RequestDetail({ request, onClose, onUpdated }) {
  const [note, setNote] = useState("");
  const [amount, setAmount] = useState("");
  const [message, setMessage] = useState("");
  const [quoteFile, setQuoteFile] = useState(null);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    setNote(""); setAmount(request?.quotation?.amount || ""); setMessage(""); setQuoteFile(null);
  }, [request]);

  if (!request) return null;

  const r = request;

  const changeStatus = async (status) => {
    setBusy("status");
    try { onUpdated(await updateStatus(r.id, status)); toast.success(`Status set to ${status}`); }
    catch { toast.error("Could not update status"); }
    finally { setBusy(""); }
  };

  const submitNote = async () => {
    if (!note.trim()) return;
    setBusy("note");
    try { onUpdated(await addNote(r.id, note.trim())); setNote(""); toast.success("Note added"); }
    catch { toast.error("Could not add note"); }
    finally { setBusy(""); }
  };

  const submitQuote = async () => {
    if (!amount.trim()) return toast.error("Enter a quote amount");
    setBusy("quote");
    try {
      const updated = await sendQuotation(r.id, amount.trim(), message.trim(), quoteFile);
      onUpdated(updated);
      toast.success(updated?.quotation?.email_sent ? "Quotation sent to customer" : "Quotation saved (email not sent)");
      setQuoteFile(null); setMessage("");
    } catch { toast.error("Could not send quotation"); }
    finally { setBusy(""); }
  };

  return (
    <Dialog open={!!request} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-3xl overflow-y-auto bg-[#f8f9f8] p-0">
        <DialogHeader className="sticky top-0 z-10 border-b border-[#e6eae7] bg-[#163323] px-6 py-4 text-white">
          <DialogTitle className="flex flex-wrap items-center gap-3 text-white">
            <span className="font-mono text-lg font-bold">{r.reference_number}</span>
            <StatusBadge status={r.status} />
            <span className="text-sm font-normal text-white/60">{fmtDate(r.created_at)}</span>
          </DialogTitle>
          <DialogDescription className="text-white/60">
            Construction Surveying request from {r.customer?.full_name}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 p-6">
          {/* Status control */}
          <div className="flex flex-col gap-2 rounded-xl border border-[#e6eae7] bg-white p-4 sm:flex-row sm:items-center sm:justify-between">
            <Label className="text-sm font-medium text-[#163323]">Update status</Label>
            <Select value={r.status} onValueChange={changeStatus} disabled={busy === "status"}>
              <SelectTrigger data-testid="admin-status-select" className="h-10 w-full rounded-lg border-[#d2dad5] bg-white sm:w-64">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <Section icon={User} title="Customer">
              <Item k="Full name" v={r.customer?.full_name} />
              <Item k="Company" v={r.customer?.company_name} />
              <Item k="Phone" v={r.customer?.phone} />
              <Item k="Email" v={r.customer?.email} />
              <Item k="Preferred contact" v={r.customer?.preferred_contact_method} />
            </Section>
            <Section icon={MapPin} title="Location">
              <Item k="Address" v={r.location?.address} />
              <Item k="Island" v={r.location?.island} />
              <Item k="Lot / parcel" v={r.location?.lot_parcel_number} />
              <Item k="Property size" v={r.location?.property_size} />
              <Item k="Site access" v={r.location?.site_access_instructions} />
            </Section>
          </div>

          <Section icon={HardHat} title="Construction Details">
            <Item k="Project type" v={r.construction?.project_type} />
            <Item k="Work required" v={(r.construction?.work_required || []).join(", ")} />
            <Item k="Stage" v={r.construction?.construction_stage} />
            <Item k="Preferred date" v={r.construction?.preferred_service_date} />
            <Item k="Description" v={r.construction?.project_description} />
            <Item k="Contractor / site manager" v={r.construction?.contractor_info} />
          </Section>

          <Section icon={Paperclip} title={`Documents (${(r.documents || []).length})`}>
            {(r.documents || []).length === 0 ? (
              <p className="text-sm text-[#5a6660]">No documents uploaded.</p>
            ) : (
              <div className="space-y-2">
                {r.documents.map((d) => (
                  <a key={d.id} data-testid="doc-link" href={fileUrl(d.storage_path)} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-3 rounded-lg border border-[#e6eae7] p-2.5 transition-colors hover:border-[#c8a452] hover:bg-[#f6f1e3]">
                    <FileText className="h-4 w-4 text-[#8e6e23]" />
                    <span className="flex-1 truncate text-sm text-[#1a221e]">{d.original_filename}</span>
                    <ExternalLink className="h-4 w-4 text-[#9aa5a0]" />
                  </a>
                ))}
              </div>
            )}
          </Section>

          {/* Internal notes */}
          <Section icon={StickyNote} title="Internal Notes">
            <div className="space-y-2">
              {(r.notes || []).length === 0 && <p className="text-sm text-[#5a6660]">No notes yet.</p>}
              {(r.notes || []).map((n) => (
                <div key={n.id} className="rounded-lg bg-[#f8f9f8] p-3">
                  <p className="text-sm text-[#1a221e]">{n.text}</p>
                  <p className="mt-1 text-xs text-[#9aa5a0]">{fmtDate(n.created_at)}</p>
                </div>
              ))}
            </div>
            <div className="mt-3 flex gap-2">
              <Textarea data-testid="note-input" rows={2} value={note} onChange={(e) => setNote(e.target.value)}
                placeholder="Add an internal note…"
                className="rounded-lg border-[#d2dad5] bg-white focus-visible:ring-2 focus-visible:ring-[#c8a452] focus-visible:ring-offset-0" />
              <Button data-testid="add-note-btn" onClick={submitNote} disabled={busy === "note" || !note.trim()}
                className="rounded-lg bg-[#163323] text-white hover:bg-[#0d2116]">
                {busy === "note" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add"}
              </Button>
            </div>
          </Section>

          {/* Quotation */}
          <Section icon={Send} title="Quotation">
            {r.quotation && (
              <div className="mb-3 rounded-lg border border-[#d8b4fe] bg-[#f3e8ff] p-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-[#7e22ce]">Sent: {r.quotation.amount}</span>
                  <span className="text-xs text-[#7e22ce]">{fmtDate(r.quotation.sent_at)}</span>
                </div>
                {r.quotation.message && <p className="mt-1 text-xs text-[#6b21a8]">{r.quotation.message}</p>}
                {r.quotation.file && (
                  <a href={fileUrl(r.quotation.file.storage_path)} target="_blank" rel="noopener noreferrer"
                    className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-[#7e22ce] hover:underline">
                    <Download className="h-3.5 w-3.5" /> {r.quotation.file.original_filename}
                  </a>
                )}
              </div>
            )}
            <div className="space-y-3">
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label className="text-xs text-[#5a6660]">Quote amount</Label>
                  <Input data-testid="quote-amount" value={amount} onChange={(e) => setAmount(e.target.value)}
                    placeholder="e.g. $1,850.00"
                    className="h-10 rounded-lg border-[#d2dad5] bg-white focus-visible:ring-2 focus-visible:ring-[#c8a452] focus-visible:ring-offset-0" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-[#5a6660]">Attach quotation (PDF/DOC)</Label>
                  <Input data-testid="quote-file" type="file" accept=".pdf,.doc,.docx"
                    onChange={(e) => setQuoteFile(e.target.files?.[0] || null)}
                    className="h-10 cursor-pointer rounded-lg border-[#d2dad5] bg-white file:mr-3 file:rounded file:border-0 file:bg-[#f6f1e3] file:px-2 file:py-1 file:text-xs file:text-[#8e6e23]" />
                </div>
              </div>
              <Textarea data-testid="quote-message" rows={2} value={message} onChange={(e) => setMessage(e.target.value)}
                placeholder="Optional message to include in the email…"
                className="rounded-lg border-[#d2dad5] bg-white focus-visible:ring-2 focus-visible:ring-[#c8a452] focus-visible:ring-offset-0" />
              <Button data-testid="send-quote-btn" onClick={submitQuote} disabled={busy === "quote"}
                className="rounded-full bg-[#c8a452] font-semibold text-[#163323] hover:bg-[#b38f3f]">
                {busy === "quote" ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending…</> : <><Send className="mr-2 h-4 w-4" /> Send quotation</>}
              </Button>
            </div>
          </Section>
        </div>
      </DialogContent>
    </Dialog>
  );
}


const InquiryBadge = ({ status }) => (
  <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${INQUIRY_STATUS_STYLES[status] || ""}`}>
    {status}
  </span>
);

function InquiriesView() {
  const [inquiries, setInquiries] = useState([]);
  const [stats, setStats] = useState({});
  const [filter, setFilter] = useState("All");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [items, st] = await Promise.all([listInquiries(filter, search), getInquiryStats()]);
      setInquiries(items);
      setStats(st);
    } catch {
      toast.error("Failed to load messages");
    } finally {
      setLoading(false);
    }
  }, [filter, search]);

  useEffect(() => { load(); }, [load]);

  const onUpdated = (updated) => {
    setSelected(updated);
    setInquiries((prev) => prev.map((i) => (i.id === updated.id ? updated : i)));
    getInquiryStats().then(setStats);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteInquiry(deleteTarget.id);
      setInquiries((prev) => prev.filter((i) => i.id !== deleteTarget.id));
      setSelected((s) => (s && s.id === deleteTarget.id ? null : s));
      getInquiryStats().then(setStats);
      toast.success(`Deleted ${deleteTarget.reference_number}`);
    } catch {
      toast.error("Could not delete message");
    } finally {
      setDeleteTarget(null);
    }
  };

  const tabs = ["All", ...INQUIRY_STATUSES];

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-5">
        {tabs.map((t) => (
          <button key={t} data-testid={`inq-stat-${t}`} onClick={() => setFilter(t)}
            className={`rounded-xl border p-3 text-left transition-all ${
              filter === t ? "border-[#c8a452] bg-[#f6f1e3]" : "border-[#e6eae7] bg-white hover:border-[#c8a452]/50"
            }`}>
            <div className="font-display text-2xl font-bold text-[#163323]">{stats[t] ?? 0}</div>
            <div className="mt-0.5 truncate text-xs text-[#5a6660]">{t}</div>
          </button>
        ))}
      </div>

      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#9aa5a0]" />
          <Input data-testid="inq-search" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by reference, name, email, company or subject…"
            className="h-11 rounded-lg border-[#d2dad5] bg-white pl-10 focus-visible:ring-2 focus-visible:ring-[#c8a452] focus-visible:ring-offset-0" />
        </div>
        <Button data-testid="inq-refresh" onClick={load} variant="outline"
          className="h-11 rounded-lg border-[#d2dad5] text-[#163323] hover:bg-[#f2f4f2]">
          <RefreshCw className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>

      <div className="overflow-hidden rounded-xl border border-[#e6eae7] bg-white">
        <div className="hidden grid-cols-12 gap-4 border-b border-[#e6eae7] bg-[#f8f9f8] px-5 py-3 text-xs font-semibold uppercase tracking-wide text-[#5a6660] md:grid">
          <div className="col-span-2">Reference</div>
          <div className="col-span-3">From</div>
          <div className="col-span-4">Subject</div>
          <div className="col-span-2">Status</div>
          <div className="col-span-1 text-right">Actions</div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16 text-[#5a6660]">
            <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading…
          </div>
        ) : inquiries.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-[#5a6660]">
            <Inbox className="h-10 w-10 text-[#c8d0cb]" />
            <p className="mt-3 text-sm">No messages found.</p>
          </div>
        ) : (
          inquiries.map((i) => (
            <div key={i.id} data-testid={`inquiry-row-${i.reference_number}`} onClick={() => setSelected(i)}
              className="grid w-full cursor-pointer grid-cols-1 gap-2 border-b border-[#e6eae7] px-5 py-4 text-left transition-colors last:border-0 hover:bg-[#f8f9f8] md:grid-cols-12 md:items-center md:gap-4">
              <div className="col-span-2 font-mono text-sm font-semibold text-[#163323]">
                {i.reference_number}
                <span className="block font-sans text-xs font-normal text-[#9aa5a0]">{fmtDate(i.created_at)}</span>
              </div>
              <div className="col-span-3">
                <div className="text-sm font-medium text-[#1a221e]">{i.full_name}</div>
                <div className="text-xs text-[#5a6660]">{i.email}</div>
              </div>
              <div className="col-span-4 truncate text-sm text-[#1a221e]">{i.subject}</div>
              <div className="col-span-2"><InquiryBadge status={i.status} /></div>
              <div className="col-span-1 flex md:justify-end">
                <button data-testid={`delete-inquiry-${i.reference_number}`}
                  onClick={(e) => { e.stopPropagation(); setDeleteTarget(i); }}
                  className="rounded-lg p-2 text-[#9aa5a0] transition-colors hover:bg-[#fef2f2] hover:text-[#b91c1c]"
                  title="Delete message" aria-label="Delete message">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      <InquiryDetail inquiry={selected} onClose={() => setSelected(null)} onUpdated={onUpdated} />

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this message?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes inquiry <strong>{deleteTarget?.reference_number}</strong> from{" "}
              {deleteTarget?.full_name}. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-inq-delete">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-inq-delete" onClick={confirmDelete}
              className="bg-[#b91c1c] text-white hover:bg-[#991b1b]">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function InquiryDetail({ inquiry, onClose, onUpdated }) {
  const [busy, setBusy] = useState(false);
  if (!inquiry) return null;
  const i = inquiry;

  const changeStatus = async (status) => {
    setBusy(true);
    try { onUpdated(await updateInquiryStatus(i.id, status)); toast.success(`Status set to ${status}`); }
    catch { toast.error("Could not update status"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={!!inquiry} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto bg-[#f8f9f8] p-0">
        <DialogHeader className="sticky top-0 z-10 border-b border-[#e6eae7] bg-[#163323] px-6 py-4 text-white">
          <DialogTitle className="flex flex-wrap items-center gap-3 text-white">
            <span className="font-mono text-lg font-bold">{i.reference_number}</span>
            <InquiryBadge status={i.status} />
            <span className="text-sm font-normal text-white/60">{fmtDate(i.created_at)}</span>
          </DialogTitle>
          <DialogDescription className="text-white/60">Contact inquiry from {i.full_name}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 p-6">
          <div className="flex flex-col gap-2 rounded-xl border border-[#e6eae7] bg-white p-4 sm:flex-row sm:items-center sm:justify-between">
            <Label className="text-sm font-medium text-[#163323]">Update status</Label>
            <Select value={i.status} onValueChange={changeStatus} disabled={busy}>
              <SelectTrigger data-testid="inquiry-status-select" className="h-10 w-full rounded-lg border-[#d2dad5] bg-white sm:w-64">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {INQUIRY_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <Section icon={User} title="Sender">
            <Item k="Full name" v={i.full_name} />
            <Item k="Company" v={i.company_name} />
            <Item k="Phone" v={i.phone} />
            <Item k="Email" v={i.email} />
            <Item k="Preferred contact" v={i.preferred_contact_method} />
          </Section>

          <Section icon={MessageSquare} title="Message">
            <Item k="Subject" v={i.subject} />
            <div className="mt-2 whitespace-pre-wrap rounded-lg bg-[#f8f9f8] p-3 text-sm text-[#1a221e]">{i.message}</div>
          </Section>

          <Section icon={Paperclip} title={`Attachments (${(i.documents || []).length})`}>
            {(i.documents || []).length === 0 ? (
              <p className="text-sm text-[#5a6660]">No attachments.</p>
            ) : (
              <div className="space-y-2">
                {i.documents.map((d) => (
                  <a key={d.id} href={fileUrl(d.storage_path)} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-3 rounded-lg border border-[#e6eae7] p-2.5 transition-colors hover:border-[#c8a452] hover:bg-[#f6f1e3]">
                    <FileText className="h-4 w-4 text-[#8e6e23]" />
                    <span className="flex-1 truncate text-sm text-[#1a221e]">{d.original_filename}</span>
                    <ExternalLink className="h-4 w-4 text-[#9aa5a0]" />
                  </a>
                ))}
              </div>
            )}
          </Section>

          <a href={`mailto:${i.email}?subject=Re: ${encodeURIComponent(i.subject)} (${i.reference_number})`}
            data-testid="inquiry-reply-btn"
            className="flex items-center justify-center gap-2 rounded-lg bg-[#163323] px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-[#0d2116]">
            <Mail className="h-4 w-4" /> Reply by Email
          </a>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SiteImagesView() {
  const [overrides, setOverrides] = useState({});
  const [loading, setLoading] = useState(true);
  const [busyKey, setBusyKey] = useState("");

  const refresh = useCallback(async () => {
    setLoading(true);
    try { setOverrides((await getSiteImages()) || {}); }
    catch { toast.error("Failed to load images"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const onUpload = async (key, file) => {
    if (!file) return;
    setBusyKey(key);
    try {
      const up = await uploadFile(file);
      await setSiteImage(key, {
        id: up.id, storage_path: up.storage_path, original_filename: up.original_filename,
        content_type: up.content_type, size: up.size,
      });
      toast.success("Photo updated");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setBusyKey(""); }
  };

  const onReset = async (key) => {
    setBusyKey(key);
    try { await deleteSiteImage(key); toast.success("Reset to default photo"); await refresh(); }
    catch { toast.error("Could not reset"); }
    finally { setBusyKey(""); }
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      <div className="mb-5 flex items-start gap-3 rounded-xl border border-[#e6eae7] bg-white p-4">
        <ImageIcon className="mt-0.5 h-5 w-5 shrink-0 text-[#b38f3f]" />
        <p className="text-sm text-[#5a6660]">
          Upload photos for any position on the public website — they replace the current image instantly.
          Use landscape JPG or PNG images (at least ~1600px wide) for best results. "Reset" restores the original photo.
        </p>
      </div>
      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#c8a452]" /></div>
      ) : SITE_IMAGE_GROUPS.map(({ group, items }) => (
        <div key={group} className="mb-8">
          <h3 className="mb-3 font-display text-sm font-semibold uppercase tracking-wide text-[#163323]">{group}</h3>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {items.map(({ key, label }) => {
              const custom = !!overrides[key]?.storage_path;
              const src = resolveImage(overrides, key);
              return (
                <div key={key} data-testid={`photo-slot-${key}`} className="overflow-hidden rounded-xl border border-[#e6eae7] bg-white">
                  <div className="relative h-40 bg-[#eef0ee]">
                    {src ? (
                      <img src={src} alt={label} className="h-full w-full object-cover" />
                    ) : (
                      <div className="flex h-full items-center justify-center text-[#a7b2ab]"><ImageIcon className="h-10 w-10" /></div>
                    )}
                    {custom && <span className="absolute left-2 top-2 rounded bg-[#163323] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-[#c8a452]">Custom</span>}
                    {busyKey === key && <div className="absolute inset-0 flex items-center justify-center bg-black/40"><Loader2 className="h-6 w-6 animate-spin text-white" /></div>}
                  </div>
                  <div className="p-3">
                    <div className="text-sm font-medium text-[#1a221e]">{label}</div>
                    <div className="mt-2 flex gap-2">
                      <label className="flex-1 cursor-pointer">
                        <span className="flex items-center justify-center gap-1.5 rounded-lg bg-[#163323] px-3 py-2 text-xs font-semibold text-white transition-colors hover:bg-[#0d2116]">
                          <Upload className="h-3.5 w-3.5" /> Upload
                        </span>
                        <input data-testid={`photo-upload-${key}`} type="file" accept=".jpg,.jpeg,.png" className="hidden"
                          disabled={busyKey === key}
                          onChange={(e) => { onUpload(key, e.target.files?.[0]); e.target.value = ""; }} />
                      </label>
                      {custom && (
                        <button data-testid={`photo-reset-${key}`} onClick={() => onReset(key)} disabled={busyKey === key}
                          className="flex items-center gap-1.5 rounded-lg border border-[#d2dad5] px-3 py-2 text-xs font-semibold text-[#5a6660] transition-colors hover:bg-[#f2f4f2]">
                          <RotateCcw className="h-3.5 w-3.5" /> Reset
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

function ProjectRow({ meta, initial, onSaved }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setForm({
      title: initial?.title ?? "", type: initial?.type ?? "", island: initial?.island ?? "",
      overview: initial?.overview ?? "", requirement: initial?.requirement ?? "",
      work_completed: initial?.work_completed ?? "", deliverables: initial?.deliverables ?? "",
    });
  }, [initial]);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const hasContent = !!(initial?.overview || initial?.requirement || initial?.work_completed || initial?.deliverables);

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        title: form.title || meta.title, type: form.type || meta.type, island: form.island || meta.island,
        overview: form.overview, requirement: form.requirement,
        work_completed: form.work_completed, deliverables: form.deliverables,
      };
      const saved = await updateProject(meta.cat, payload);
      onSaved(meta.cat, saved);
      toast.success(`${meta.service} details saved`);
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save project");
    } finally { setBusy(false); }
  };

  const clearContent = async () => {
    setBusy(true);
    try {
      const saved = await updateProject(meta.cat, {
        title: "", type: "", island: "", overview: "", requirement: "", work_completed: "", deliverables: "",
      });
      onSaved(meta.cat, saved);
      setForm({ title: "", type: "", island: "", overview: "", requirement: "", work_completed: "", deliverables: "" });
      toast.success(`${meta.service} reset to default`);
    } catch {
      toast.error("Could not reset project");
    } finally { setBusy(false); }
  };

  const fld = "rounded-lg border-[#d2dad5] bg-white focus-visible:ring-2 focus-visible:ring-[#c8a452] focus-visible:ring-offset-0";

  return (
    <div data-testid={`project-admin-${meta.cat}`} className="overflow-hidden rounded-xl border border-[#e6eae7] bg-white">
      <button data-testid={`project-toggle-${meta.cat}`} onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between gap-3 px-5 py-4 text-left transition-colors hover:bg-[#f8f9f8]">
        <div>
          <div className="font-serif text-base font-bold text-[#163323]">{form.title || meta.title}</div>
          <div className="text-xs text-[#5a6660]">{meta.service}</div>
        </div>
        <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${hasContent ? "bg-[#ecfdf5] text-[#047857]" : "bg-[#fff8e6] text-[#b27b00]"}`}>
          {hasContent ? "Published" : "Not filled"}
        </span>
      </button>
      {open && (
        <div className="space-y-4 border-t border-[#e6eae7] p-5">
          {PROJECT_DETAIL_FIELDS.map(({ key, label, multiline }) => (
            <div key={key} className="space-y-1.5">
              <Label className="text-sm font-medium text-[#1a221e]">{label}</Label>
              {multiline ? (
                <Textarea data-testid={`project-${meta.cat}-${key}`} rows={3} value={form[key] || ""}
                  onChange={(e) => upd(key, e.target.value)} className={fld}
                  placeholder={`Enter ${label.toLowerCase()}…`} />
              ) : (
                <Input data-testid={`project-${meta.cat}-${key}`} value={form[key] || ""}
                  onChange={(e) => upd(key, e.target.value)} className={`h-11 ${fld}`}
                  placeholder={meta[key] || label} />
              )}
            </div>
          ))}
          <div className="flex gap-2">
            <Button data-testid={`project-save-${meta.cat}`} onClick={save} disabled={busy}
              className="h-11 flex-1 rounded-lg bg-[#163323] font-semibold text-white hover:bg-[#0d2116] disabled:opacity-60">
              {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Save Project Details"}
            </Button>
            {hasContent && (
              <Button data-testid={`project-reset-${meta.cat}`} onClick={clearContent} disabled={busy}
                variant="outline" className="h-11 rounded-lg border-[#d2dad5] font-semibold text-[#5a6660] hover:bg-[#f2f4f2]">
                <RotateCcw className="mr-1.5 h-4 w-4" /> Reset
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function ProjectsAdminView() {
  const [overrides, setOverrides] = useState({});
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try { setOverrides((await getProjects()) || {}); }
    catch { toast.error("Failed to load projects"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const onSaved = (cat, saved) => setOverrides((prev) => ({ ...prev, [cat]: saved }));

  return (
    <div className="mx-auto max-w-4xl px-4 py-8 sm:px-6 lg:px-8">
      <div className="mb-5 flex items-start gap-3 rounded-xl border border-[#e6eae7] bg-white p-4">
        <HardHat className="mt-0.5 h-5 w-5 shrink-0 text-[#b38f3f]" />
        <p className="text-sm text-[#5a6660]">
          Fill in the details for each project shown on the public Projects page. Leave a field blank to keep the
          default placeholder text. Photos for each project are managed in the "Photos" tab.
        </p>
      </div>
      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#c8a452]" /></div>
      ) : (
        <div className="space-y-3">
          {PROJECT_CATS.map((meta) => (
            <ProjectRow key={meta.cat} meta={meta} initial={overrides[meta.cat]} onSaved={onSaved} />
          ))}
        </div>
      )}
    </div>
  );
}
